import React, { useState } from "react";
import styled from "styled-components";
import { theme } from "../../Themes/Themes";
import NavigateBeforeRoundedIcon from "@mui/icons-material/NavigateBeforeRounded";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";
import HelpOutlineSharpIcon from "@mui/icons-material/HelpOutlineSharp";
import playAndStepsGif from "../../Media/gifs/play-and-steps.gif";
import closeAndNewGif from "../../Media/gifs/close-and-new.gif";
import contextAndIoGif from "../../Media/gifs/context-and-io.gif";
import customUsecaseGif from "../../Media/gifs/custom-usecase.gif";
import chatGif from "../../Media/gifs/trimmed-chat-with-completion.gif";
import saveGif from "../../Media/gifs/save.gif";

interface TutorialStep {
  title: string;
  description: string;
  gifUrl?: string;
}

interface SimulationTutorialProps {
  isPopup?: boolean;
  onClose?: () => void;
  isOverlayMode?: boolean;
}

const TutorialContainer = styled.div<{
  isPopup: boolean;
  isOverlayMode?: boolean;
}>`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${(props) => (props.isOverlayMode ? "10px" : "20px")};
  position: relative;
  overflow: hidden;
  height: ${(props) => (props.isOverlayMode ? "100%" : "auto")};
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  overflow: hidden;
  padding: 0 0;
`;

const CarouselViewport = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  padding: 0 60px; // Space for navigation buttons
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  pointer-events: none;
  box-sizing: border-box;
`;

const CarouselTrack = styled.div<{ offset: number }>`
  display: flex;
  gap: 20px;
  transition: transform 0.5s ease;
  transform: translateX(${(props) => props.offset}px);
  pointer-events: auto;
`;

const StepCard = styled.div<{ isActive: boolean; isOverlayMode?: boolean }>`
  background: ${theme.custom.lightestGrey};
  color: ${theme.custom.darkGrey};
  padding: ${(props) => (props.isOverlayMode ? "20px" : "24px")};
  border-radius: 24px;
  transition: all 0.3s ease;
  width: 1000px;
  flex-shrink: 0;
  opacity: ${(props) => (props.isActive ? 1 : 0.7)};
  border: 1px solid ${theme.custom.lightGrey};
  height: auto;
  max-height: ${(props) =>
    props.isOverlayMode ? "calc(100vh - 120px)" : "none"};
  box-sizing: border-box;
  box-shadow: ${(props) =>
    props.isActive
      ? "0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)"
      : "0 2px 8px rgba(0, 0, 0, 0.04)"};

  h3 {
    margin: 0 0 ${(props) => (props.isOverlayMode ? "12px" : "16px")} 0;
    font-size: ${(props) => (props.isOverlayMode ? "0.9rem" : "1rem")};
    font-weight: 600;
    color: ${theme.custom.evenDarkerThanDeepestDarkestGrey};
    letter-spacing: 0.2px;
  }
`;

const StepContent = styled.div<{ isOverlayMode?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${(props) => (props.isOverlayMode ? "12px" : "16px")};
`;

const GifContainer = styled.div<{ isOverlayMode?: boolean }>`
  height: ${(props) => (props.isOverlayMode ? "55vh" : "50vh")};
  max-height: ${(props) => (props.isOverlayMode ? "500px" : "none")};
  width: auto;
  background: ${theme.custom.lightGrey};
  border-radius: 16px;
  overflow: hidden;
  flex-shrink: 0;
  border: 1px solid ${theme.custom.lightGrey};

  img {
    height: 100%;
    width: auto;
    object-fit: contain;
  }
`;

const StepDescription = styled.p<{ isOverlayMode?: boolean }>`
  flex: 1;
  margin: 0;
  font-size: ${(props) => (props.isOverlayMode ? "0.8rem" : "0.85rem")};
  line-height: 1.3;
  color: ${theme.custom.darkestGrey};
  text-align: center;
  max-height: ${(props) => (props.isOverlayMode ? "2.5em" : "3em")};
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: ${(props) => (props.isOverlayMode ? "2" : "2")};
  -webkit-box-orient: vertical;
`;

const NavigationButton = styled.button`
  position: absolute;
  z-index: 2;
  background: ${theme.custom.lightestGrey};
  border: 1px solid ${theme.custom.lightGrey};
  color: ${theme.custom.darkestGrey};
  cursor: pointer;
  padding: 15px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  top: 50%;
  transform: translateY(-50%);

  &:hover {
    background: ${theme.custom.paleGrey};
    transform: translateY(-50%) scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    &:hover {
      background: ${theme.custom.lightestGrey};
      transform: translateY(-50%);
    }
  }

  svg {
    width: 32px;
    height: 32px;
    fill: ${theme.custom.lighterPink};
  }

  &.prev {
    left: 10px;
  }

  &.next {
    right: 10px;
  }
`;

const StepIndicators = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 20px;
`;

const StepDot = styled.div<{ isActive: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) =>
    props.isActive ? theme.custom.pink : theme.custom.lightGrey};
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: scale(1.2);
  }
`;

const HelpText = styled.div`
  text-align: center;
  margin-top: 16px;
  color: ${theme.custom.darkGrey};
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  .helpIcon {
    font-size: 14px;
    color: ${theme.custom.pink};
  }
`;

const steps: TutorialStep[] = [
  {
    title: "Overview & Steps",
    description:
      "Click play to see the overview of a use case and then select specific steps, or click the arrows to follow procedurally through a simulation!",
    gifUrl: playAndStepsGif,
  },
  {
    title: "System & Path Levels",
    description:
      "Swap quickly between the system level, and simulation path levels of scope for context specific detail. Check inputs and outputs between steps.",
    gifUrl: contextAndIoGif,
  },
  {
    title: "Return & Explore",
    description:
      "Close the simulation to get back to exploring and scroll down to deep dive other use cases!",
    gifUrl: closeAndNewGif,
  },
  {
    title: "Ask & Discover",
    description:
      "Interested in something specific outside of the major use cases? Just ask!",
    gifUrl: customUsecaseGif,
  },
  {
    title: "Analyze & Plan",
    description:
      "Use the simulations to provide exactly the context to ask for pros and cons, weaknesses, bugs or even develop a plan in the Chat tab.",
    gifUrl: chatGif,
  },
  {
    title: "Save & Collaborate",
    description:
      "Save your diagram and simulations in your local repository, where you can push to a branch so your team can get up to speed and build together quickly!\n Alternatively, login with github to push the diagram file to the repo.",
    gifUrl: saveGif,
  },
];

const SimulationTutorial: React.FC<SimulationTutorialProps> = ({
  isPopup = false,
  onClose,
  isOverlayMode = false,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);
  const viewportRef = React.useRef<HTMLDivElement>(null);
  const trackRef = React.useRef<HTMLDivElement>(null);
  const firstCardRef = React.useRef<HTMLDivElement>(null);
  const cardWidth = 1000; // Default card width
  const cardGap = 20; // Gap between cards from CarouselTrack

  // Handle window resize to recalculate centering
  React.useEffect(() => {
    const handleResize = () => {
      forceUpdate();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Force recalculation when active step changes
  React.useLayoutEffect(() => {
    // Small delay to ensure DOM is updated
    const timer = setTimeout(() => {
      forceUpdate();
    }, 10);
    return () => clearTimeout(timer);
  }, [activeStep]);

  const handlePrevStep = () => {
    setActiveStep((prev) => Math.max(0, prev - 1));
  };

  const handleNextStep = () => {
    setActiveStep((prev) => Math.min(steps.length - 1, prev + 1));
  };

  // Calculate the offset to center the active card
  const calculateOffset = () => {
    if (!viewportRef.current) return 0;

    // Get viewport dimensions
    const viewport = viewportRef.current;
    const viewportWidth = viewport.offsetWidth;

    // Fixed measurements - we know these from the styled components
    const CARD_WIDTH = 1000;
    const CARD_GAP = 20;
    const VIEWPORT_PADDING = 120; // 60px on each side

    // Calculate the content area width
    const contentWidth = viewportWidth - VIEWPORT_PADDING;

    // Calculate where the center of the content area is
    const contentCenter = contentWidth / 2;

    // Calculate the position of the active card
    // Each card takes up CARD_WIDTH + CARD_GAP space (except the last one has no gap after it)
    const activeCardStartPosition = activeStep * (CARD_WIDTH + CARD_GAP);
    const activeCardCenter = activeCardStartPosition + CARD_WIDTH / 2;

    // Calculate the offset to center the active card
    // We want to move the track left so that the active card's center aligns with the content center
    const offset = contentCenter - activeCardCenter;

    return offset;
  };

  return (
    <TutorialContainer isPopup={isPopup} isOverlayMode={isOverlayMode}>
      <CarouselContainer>
        <NavigationButton
          className="prev"
          onClick={handlePrevStep}
          disabled={activeStep === 0}
          aria-label="Previous step"
        >
          <NavigateBeforeRoundedIcon />
        </NavigationButton>

        <CarouselViewport ref={viewportRef}>
          <CarouselTrack ref={trackRef} offset={calculateOffset()}>
            {steps.map((step, index) => (
              <StepCard
                key={index}
                isActive={index === activeStep}
                isOverlayMode={isOverlayMode}
                ref={index === 0 ? firstCardRef : undefined}
              >
                <h3>{step.title}</h3>
                <StepContent isOverlayMode={isOverlayMode}>
                  {step.gifUrl && (
                    <GifContainer isOverlayMode={isOverlayMode}>
                      <img
                        src={step.gifUrl}
                        alt={`Tutorial step ${index + 1}`}
                      />
                    </GifContainer>
                  )}
                  <StepDescription isOverlayMode={isOverlayMode}>
                    {step.description}
                  </StepDescription>
                </StepContent>
              </StepCard>
            ))}
          </CarouselTrack>
        </CarouselViewport>

        <NavigationButton
          className="next"
          onClick={handleNextStep}
          disabled={activeStep === steps.length - 1}
          aria-label="Next step"
        >
          <NavigateNextRoundedIcon />
        </NavigationButton>
      </CarouselContainer>

      <StepIndicators>
        {steps.map((_, index) => (
          <StepDot
            key={index}
            isActive={index === activeStep}
            onClick={() => setActiveStep(index)}
          />
        ))}
      </StepIndicators>
      <HelpText>
        {isPopup && "Click anywhere outside to close. "}You can open this guide
        and other resources by clicking the help button in the top right
        <HelpOutlineSharpIcon className="helpIcon" /> .
      </HelpText>
    </TutorialContainer>
  );
};

export default SimulationTutorial;
